import { useEffect, useState } from "react";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (v) => `${(Number(v) || 0).toFixed(2).replace(".", ",")} €`;

const downloadBlob = async (url, filename, opts = {}) => {
  const token = localStorage.getItem("token");
  const res = await fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return res;
};

/** "Generar devolución" — devolución parcial por producto de una factura ya
 * emitida. Lista los cursos sueltos y paquetes devolvibles (con lo ya
 * devuelto deshabilitado), exige un motivo, y genera la factura
 * rectificativa (R-AÑO-0000) al confirmar. */
export default function CreditNoteModal({ txId, invoiceNumber, open, onClose, onDone }) {
  const [lines, setLines] = useState(null); // {loose, packages} | null = loading
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLines(null);
    setSelectedCourses([]);
    setSelectedPackages([]);
    setReason("");
    try {
      const { data } = await api.get(`/admin/tx/${txId}/returnable-lines`);
      setLines(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar la factura");
      setLines({ loose: [], packages: [] });
    }
  };

  useEffect(() => {
    if (open && txId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, txId]);

  const toggleCourse = (id) =>
    setSelectedCourses((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const togglePackage = (id) =>
    setSelectedPackages((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const totalCredit =
    (lines?.loose || [])
      .filter((ln) => selectedCourses.includes(ln.line_id))
      .reduce((s, ln) => s + ln.credit_amount, 0) +
    (lines?.packages || [])
      .filter((p) => selectedPackages.includes(p.package_id))
      .reduce((s, p) => s + p.credit_amount, 0);

  const canSubmit = (selectedCourses.length > 0 || selectedPackages.length > 0) && reason.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) {
      if (!reason.trim()) toast.error("El motivo de la devolución es obligatorio");
      else toast.error("Selecciona al menos un producto a devolver");
      return;
    }
    setSaving(true);
    try {
      const res = await downloadBlob(
        `${API}/admin/tx/${txId}/credit-note`,
        `rectificativa.pdf`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            returned_course_ids: selectedCourses,
            returned_package_ids: selectedPackages,
            reason: reason.trim(),
          }),
        },
      );
      const creditNo = res.headers.get("X-Credit-Note-Number");
      toast.success(`Devolución ${creditNo || ""} generada`);
      onDone?.();
    } catch (err) {
      toast.error(err.message || "No se pudo generar la devolución");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="credit-note-modal">
        <DialogHeader>
          <DialogTitle>Generar devolución — Factura {invoiceNumber}</DialogTitle>
        </DialogHeader>

        {lines === null ? (
          <p className="text-sm text-gray-500 py-8 text-center">
            <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Cargando…
          </p>
        ) : (
          <div className="space-y-4">
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
              {lines.loose.map((ln) => (
                <label
                  key={ln.line_id}
                  className={`flex items-center gap-3 px-3 py-2 ${
                    ln.already_returned ? "opacity-50" : "cursor-pointer hover:bg-gray-50"
                  }`}
                  data-testid={`credit-note-course-${ln.line_id}`}
                >
                  <Checkbox
                    checked={selectedCourses.includes(ln.line_id)}
                    disabled={ln.already_returned}
                    onCheckedChange={() => toggleCourse(ln.line_id)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-brand-ink truncate">{ln.title}</p>
                    {ln.already_returned && <p className="text-[11px] text-gray-400">Ya devuelto</p>}
                  </div>
                  <span className="text-xs font-mono-data text-gray-600">{fmtEur(ln.credit_amount)}</span>
                </label>
              ))}
              {lines.packages.map((p) => (
                <label
                  key={p.package_id}
                  className={`flex items-center gap-3 px-3 py-2 ${
                    p.already_returned ? "opacity-50" : "cursor-pointer hover:bg-gray-50"
                  }`}
                  data-testid={`credit-note-package-${p.package_id}`}
                >
                  <Checkbox
                    checked={selectedPackages.includes(p.package_id)}
                    disabled={p.already_returned}
                    onCheckedChange={() => togglePackage(p.package_id)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-brand-ink truncate">
                      📦 Paquete: {p.name}
                      <span className="ml-1 text-[10px] font-normal text-purple-700">completo</span>
                    </p>
                    <p className="text-[11px] text-gray-400 truncate" title={p.course_titles.join(" + ")}>
                      {p.course_titles.join(" + ")}
                    </p>
                    {p.already_returned && <p className="text-[11px] text-gray-400">Ya devuelto</p>}
                  </div>
                  <span className="text-xs font-mono-data text-gray-600">{fmtEur(p.credit_amount)}</span>
                </label>
              ))}
              {lines.loose.length === 0 && lines.packages.length === 0 && (
                <p className="text-center text-xs text-gray-400 py-6">Esta factura no tiene productos devolvibles.</p>
              )}
            </div>

            {lines.packages.length > 0 && (
              <p
                className="text-[11px] text-purple-900 bg-purple-50 border border-purple-200 rounded-md p-2"
                data-testid="credit-note-pack-note"
              >
                Los paquetes se devuelven <strong>completos</strong>, no curso a curso: su precio
                cerrado depende de comprarlos juntos, así que separarlos dejaría mal aplicado el
                descuento.
              </p>
            )}

            <div>
              <Label htmlFor="cn-reason">Motivo de la devolución *</Label>
              <Textarea
                id="cn-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explica por qué se devuelve — aparecerá en la factura rectificativa"
                data-testid="credit-note-reason"
              />
            </div>

            {(selectedCourses.length > 0 || selectedPackages.length > 0) && (
              <div className="flex items-center justify-between text-sm bg-red-50 border border-red-200 rounded-md px-3 py-2">
                <span className="text-red-800">Importe a devolver (IVA incluido)</span>
                <span className="font-mono-data font-semibold text-red-700" data-testid="credit-note-total">
                  − {fmtEur(totalCredit)}
                </span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={saving || lines === null || !canSubmit}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="credit-note-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
            Generar devolución
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
